// src/hooks/liveStreamManagment/useMonitorStream.ts

import { useEffect, useRef, useState, useCallback } from 'react';
import { SourceRegistry      } from '../../dataTypes/liveStreamManagment/sourceRegistry/SourceRegistry';
import { HlsAcquirer         } from '../../dataTypes/liveStreamManagment/sourceRegistry/acquirers/HlsAcquirer';
import { AudioSourceRegistry } from '../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';

const MONITOR_SOURCE_ID       = 'hls-monitor-retour';
const MONITOR_AUDIO_SOURCE_ID = 'audio-hls-monitor-retour';

// ── Sonde de disponibilité du flux ────────────────────────────────────────────
//
// LE PROBLÈME (mesuré le 21/08/2026, cf. latence-demarrage-chaine-2026-08-21.md).
// Ce hook démarre dès que `channelStatus` vaut 'active', c'est-à-dire dès que le
// CONTAINER de la chaîne tourne — une quinzaine de secondes avant qu'un flux
// existe. Toutes les acquisitions de cette fenêtre échouent forcément : HAProxy
// répond 410 tant que la chaîne n'est pas dans sa map, puis MediaMTX 404 tant que
// le premier segment n'est pas écrit. Or chaque tentative coûtait cher :
//
//   - ~3 s de retries hls.js (manifestLoadingMaxRetry: 2) avec, à chaque coup, la
//     création puis la destruction d'une instance hls.js et d'un <video> caché ;
//   - puis un backoff qui GRANDIT : 2 s, 3 s, 4 s, 5 s.
//
// Résultat : la tentative qui finit par réussir pouvait démarrer jusqu'à 8 s
// après que le flux soit réellement disponible. Sur les 37 s mesurées avant
// correctifs, c'était le deuxième poste.
//
// LA SONDE. Un simple GET du .m3u8 dit en une requête si le flux est là. Tant
// qu'il ne l'est pas, on repose 1 s et on redemande — sans jamais construire de
// lecteur. L'acquisition complète n'est tentée qu'une fois le manifeste servi,
// donc au plus 1 s après sa mise à disposition.
//
// Toute erreur est traitée comme « pas encore prêt », y compris un rejet CORS :
// la réponse 410 de HAProxy est produite par `http-request return`, sans garantie
// de porter les en-têtes CORS, et le navigateur la transforme alors en TypeError.
// C'est exactement le cas « pas prêt », il n'y a rien à distinguer.
const PROBE_INTERVAL_MS = 1000;

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

async function waitForStreamAvailable(url: string, cancelled: () => boolean): Promise<boolean> {
  let attempts = 0;
  const tProbe = performance.now();

  while (!cancelled()) {
    attempts += 1;

    try {
      // PAS de `credentials: 'include'` ici, contrairement à hls.js sur ce même
      // flux (cf. needsCookies dans HlsAcquirer). Constaté le 08/09/2026 :
      // MediaMTX (fmp4) pose un cookie hlsSession sur CHAQUE requête à
      // index.m3u8 et en RÉÉMET un NOUVEAU à chaque fois — et dès qu'un cookie
      // existe déjà, il cesse d'écrire `?session=` sur les URI du manifeste,
      // misant tout sur ce cookie. Cette sonde tournant en parallèle du
      // chargement réel par hls.js, son propre cookie fait sauter celui que
      // hls.js s'apprêtait à utiliser — 401 sur la sous-playlist qui suit. La
      // sonde n'a besoin d'aucune session : `index.m3u8` répond 200 sans
      // cookie dans tous les cas testés, seule la sous-playlist l'exige.
      const response = await fetch(url, { method: 'GET', cache: 'no-store' });

      if (response.ok) {
        console.log(`[useMonitorStream] 📡 flux disponible (HTTP ${response.status}) après ${attempts} sondage(s)`);
        // En `warn` À DESSEIN : GLOBAL_REMOTE_LEVELS ne remonte que warn/error au
        // serveur, et c'est la seule mesure fiable du partage entre « attendre le
        // flux » et « amorcer hls.js ». Sans ça, le diagnostic dépend de quelqu'un
        // qui a la console ouverte au bon moment.
        console.warn(`[useMonitorStream] ⏱️ manifeste servi après ${((performance.now() - tProbe) / 1000).toFixed(1)} s de sonde (${attempts} sondage(s))`);
        return true;
      }

      // 410 = chaîne absente de la map HAProxy, 404 = pas encore de segment.
      if (attempts === 1 || attempts % 5 === 0) {
        console.log(`[useMonitorStream] ⏳ flux pas encore là (HTTP ${response.status}), sondage ${attempts}`);
      }
    } catch (err) {
      if (attempts === 1 || attempts % 5 === 0) {
        console.log(`[useMonitorStream] ⏳ flux injoignable (sondage ${attempts}) :`, err);
      }
    }

    await sleep(PROBE_INTERVAL_MS);
  }

  return false;
}

export interface UseMonitorStreamResult {
  mediaStream         : MediaStream | null;
  audioSourceRegistry : AudioSourceRegistry;
  audioSourceId       : string;
  resetMonitorStream  : () => void;
  isBuffering         : boolean;
  hasSignal           : boolean;
}

export function useMonitorStream(hlsUrl: string, active: boolean): UseMonitorStreamResult {

  const registryRef      = useRef<SourceRegistry      | null>(null);
  const acquirerRef      = useRef<HlsAcquirer         | null>(null);
  const audioRegistryRef = useRef<AudioSourceRegistry>(new AudioSourceRegistry());

  const [mediaStream,  setMediaStream]  = useState<MediaStream | null>(null);
  const [resetCounter, setResetCounter] = useState(0);
  const [isBuffering,  setIsBuffering]  = useState(false);
  // true dès que le flux a affiché au moins une image depuis la dernière tentative
  // (dernier hlsUrl/active/reset) — reste true tant que la tentative en cours n'est
  // pas relancée, même si le flux stalle momentanément ensuite (cf. isBuffering).
  const [hasSignal,    setHasSignal]    = useState(false);
  const retryCountRef = useRef(0);

  const resetMonitorStream = useCallback(() => {
    console.log('[useMonitorStream] 🔄 reset demandé');
    setMediaStream(null);
    setResetCounter(c => c + 1);
  }, []);

  useEffect(() => {
    console.log(`[useMonitorStream] ⚡ useEffect déclenché — active=${active} hlsUrl=${hlsUrl} reset#${resetCounter}`);

    // Cleanup de l'instance précédente
    if (registryRef.current) {
      console.log('[useMonitorStream] 🧹 cleanup instance précédente');
      audioRegistryRef.current.forceRelease(MONITOR_AUDIO_SOURCE_ID);
      registryRef.current.destroy();
      registryRef.current = null;
      acquirerRef.current = null;
    }

    setMediaStream(null);
    setIsBuffering(false);
    setHasSignal(false);
    retryCountRef.current = 0;
    console.log('[useMonitorStream] 🔲 mediaStream → null, isBuffering → false, hasSignal → false');

    if (!hlsUrl || !active) {
      console.log(`[useMonitorStream] ⛔ arrêt ici — hlsUrl="${hlsUrl}" active=${active}`);
      return;
    }

    console.log('[useMonitorStream] 🚀 démarrage acquisition HLS');

    const registry      = new SourceRegistry();
    const acquirer      = new HlsAcquirer(registry);
    const audioRegistry = audioRegistryRef.current;

    registry.registerAcquirer('hls', acquirer);
    acquirer.setAudioSourceRegistry(audioRegistry);

    acquirer.onBufferingChange = (sourceId, buffering) => {
      if (sourceId === MONITOR_SOURCE_ID) {
        console.log(`[useMonitorStream] 📡 onBufferingChange — isBuffering=${buffering}`);
        setIsBuffering(buffering);
      }
    };

    registryRef.current = registry;
    acquirerRef.current = acquirer;

    registry.register({
      id    : MONITOR_SOURCE_ID,
      label : 'Retour monitor',
      config: { type: 'hls', url: hlsUrl, loop: true, autoRetry: true },
    });

    console.log(`[useMonitorStream] 📋 source enregistrée : ${MONITOR_SOURCE_ID}`);

    let borrowHandle: Awaited<ReturnType<SourceRegistry['borrow']>> | null = null;
    let cancelled = false;

    const acquire = async () => {
      const tAcquire = performance.now();
      // On ne construit un lecteur qu'une fois le manifeste réellement servi —
      // sinon on paie ~3 s de retries hls.js pour rien, à chaque tour (cf.
      // waitForStreamAvailable). Sortie immédiate si l'effet a été nettoyé
      // pendant l'attente : le flux peut mettre une minute à arriver, et
      // l'opérateur peut très bien fermer la fiche entre-temps.
      const available = await waitForStreamAvailable(hlsUrl, () => cancelled);

      if (!available) {
        console.log('[useMonitorStream] ❌ sonde interrompue (effet nettoyé) — pas d\'acquisition');
        return;
      }

      console.log('[useMonitorStream] 🎯 borrow() en cours...');
      try {
        borrowHandle = await registry.borrow(MONITOR_SOURCE_ID);

        if (cancelled) {
          console.log('[useMonitorStream] ❌ annulé après borrow — release');
          borrowHandle.release();
          return;
        }

        const stream = borrowHandle.stream;

        console.log('[useMonitorStream] ✅ stream acquis —', {
          id         : stream.id,
          audioTracks: stream.getAudioTracks().length,
          videoTracks: stream.getVideoTracks().length,
          tracks     : stream.getTracks().map(t => ({
            kind      : t.kind,
            enabled   : t.enabled,
            muted     : t.muted,
            readyState: t.readyState,
          })),
        });

        if (stream.getAudioTracks().length > 0) {
          try {
            audioRegistry.borrowFromStream(MONITOR_AUDIO_SOURCE_ID, stream, 'url', false);
            audioRegistry.setGain(MONITOR_AUDIO_SOURCE_ID, 1);         // signal traverse gainNode → analyseurs actifs
            // Sortie silencieuse par défaut : ce sont les HlsAudioPanel branchés sur
            // cette entrée qui décident du niveau audible. Ils réappliquent leur fader
            // dès qu'ils détectent que l'entrée a été recréée (AudioSourceRegistry.
            // getGeneration) — c'est ce qui rétablit le son après une réacquisition.
            audioRegistry.setMonitorGain(MONITOR_AUDIO_SOURCE_ID, 0);
            console.log('[useMonitorStream] 🔊 audioSourceRegistry — borrowFromStream OK');
          } catch (e) {
            console.warn('[useMonitorStream] ⚠️ audioSourceRegistry — borrowFromStream échoué :', e);
          }
        } else {
          console.warn('[useMonitorStream] ⚠️ aucune piste audio dans le stream');
        }

        setMediaStream(stream);
        // À ce stade, la vidéo interne a déjà atteint canplay/playing (cf. HlsAcquirer.acquire :
        // play() → _waitCanPlay() → 80ms de stabilisation → captureStream()) : le flux est bien arrivé.
        // Ne pas se fier à onBufferingChange ici — ses listeners ne sont attachés qu'après ce point,
        // donc ils ratent ce tout premier canplay/playing et ne réagiraient qu'à un futur stall.
        setHasSignal(true);
        console.log(`[useMonitorStream] 📺 mediaStream mis à jour — id=${stream.id}`);
        // Même raison : ce chiffre moins celui de la sonde donne le coût d'amorçage
        // de hls.js (manifeste + init.mp4 + segments avant canplay), qui est la
        // dernière poche non traitée.
        console.warn(`[useMonitorStream] ⏱️ première image à +${((performance.now() - tAcquire) / 1000).toFixed(1)} s (début d'acquisition → canplay)`);

      } catch (err) {
        if (!cancelled) {
          retryCountRef.current += 1;
          // Délai fixe et court : le backoff progressif (2 → 5 s) servait à
          // espacer des tentatives dont on savait qu'elles échoueraient pendant
          // le démarrage de la chaîne. Ce cas est désormais traité en amont par
          // la sonde, et il ne reste ici que les échecs SURVENUS ALORS QUE le
          // manifeste était servi — un flux qui vient de se retirer, une
          // acquisition qui a raté. Attendre plus longtemps à chaque coup ne
          // fait alors que retarder la reprise. Le nouveau tour repasse de toute
          // façon par la sonde, qui absorbe une éventuelle indisponibilité.
          const delay = 2000;
          console.warn(`[useMonitorStream] ⚠️ acquisition échouée (tentative ${retryCountRef.current}), retry dans ${delay}ms :`, err);
          setTimeout(() => { if (!cancelled) acquire(); }, delay);
        } else {
          console.log('[useMonitorStream] ❌ acquisition échouée mais déjà annulé, pas de retry');
        }
      }
    };

    acquire();

    return () => {
      console.log('[useMonitorStream] 🧹 cleanup effect — cancelled=true');
      cancelled = true;
      if (acquirerRef.current) acquirerRef.current.onBufferingChange = null;
      borrowHandle?.release();
      audioRegistry.forceRelease(MONITOR_AUDIO_SOURCE_ID);
      registry.destroy();
      registryRef.current = null;
      acquirerRef.current = null;
      console.log('[useMonitorStream] 🧹 cleanup terminé');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hlsUrl, active, resetCounter]);

  return {
    mediaStream,
    audioSourceRegistry : audioRegistryRef.current,
    audioSourceId       : MONITOR_AUDIO_SOURCE_ID,
    resetMonitorStream,
    isBuffering,
    hasSignal,
  };
}